<template>
	<v-container fluid>
		<v-row>
			<v-col cols="12">
				<v-card>
					<v-data-table-server
						v-model:items-per-page="pageSize"
						:headers="headers"
						:items="underwriters"
						:items-length="totalCount"
						:loading="loading"
						:page="page"
						:items-per-page-options="[10, 25, 50, 100]"
						density="comfortable"
						hover
						@update:options="onTableOptions"
					>
						<template #top>
							<v-toolbar flat>
								<v-toolbar-title>Underwriter Management</v-toolbar-title>
								<v-divider class="mx-4" inset vertical />
								<v-text-field
									v-model="lastNameSearch"
									label="Search by last name"
									prepend-inner-icon="mdi-magnify"
									variant="outlined"
									density="compact"
									hide-details
									clearable
									class="mr-4 search-field"
									@update:model-value="searchDebounced"
									@keydown.enter.prevent="searchNow"
								/>
								<v-checkbox
									v-model="includeExpired"
									label="Include Expired"
									hide-details
									class="mr-4"
									@update:model-value="onFilterChange"
								/>
								<v-chip class="mr-2">{{ totalCount }} records</v-chip>
								<m-btn color="primary" prepend-icon="mdi-plus" :to="{ path: '/SSAM/underwriters/new' }">
									Add Underwriter
								</m-btn>
							</v-toolbar>
						</template>

						<template #[`item.status`]="{ item }">
						<v-chip
							:color="item.firstName?.toUpperCase().startsWith('Z ') ? 'warning' : (item.status === 'A' ? 'success' : 'default')"
							size="small"
							label
						>
							{{ item.firstName?.toUpperCase().startsWith('Z ') ? 'Expired' : (item.status === 'A' ? 'Active' : item.status) }}
							</v-chip>
						</template>

						<template #[`item.profile`]="{ item }">{{ item.profile?.toUpperCase() }}</template>
						<template #[`item.lastName`]="{ item }">{{ item.lastName?.toUpperCase() }}</template>
						<template #[`item.firstName`]="{ item }">{{ item.firstName?.toUpperCase() }}</template>
						<template #[`item.middleName`]="{ item }">{{ item.middleName?.toUpperCase() }}</template>
						<template #[`item.userGroupProfile`]="{ item }">{{ item.userGroupProfile?.toUpperCase() }}</template>

						<template #[`item.actions`]="{ item }">
							<v-btn
								variant="tonal"
								color="warning"
								size="small"
								prepend-icon="mdi-archive-arrow-down-outline"
								:aria-label="`Expire ${item.profile}`"
								@click="confirmExpire(item)"
							>
								Expire
							</v-btn>
						</template>
					</v-data-table-server>
				</v-card>
			</v-col>
		</v-row>

		<v-dialog v-model="showExpireDialog" max-width="480">
			<v-card>
				<v-card-title class="text-title-large">Expire underwriter?</v-card-title>
				<v-card-text>
					This marks
					<strong>{{ pendingExpire?.profile?.toUpperCase() }}</strong>
					as expired by prefixing the first name with <strong>&quot;Z &quot;</strong>
					(for example, <em>DAVID OAKE</em> becomes <em>Z DAVID OAKE</em>).
					The entry is then hidden from the list unless <em>Include Expired</em> is checked.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<m-btn :disabled="expiring" @click="showExpireDialog = false">Cancel</m-btn>
					<m-btn color="warning" :loading="expiring" @click="executeExpire">Expire</m-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script lang="ts">
import { ref, inject } from 'vue';
import { useRoute } from 'vue-router';
import { useUnderwriterApi } from '@/composables/UseUnderwriterApi';
import type { UnderwriterViewModel } from '@/models/UnderwriterModels';
import MBtn from '@/components/BaseButton.vue';
import { Tell } from '@/services/Tell';

export default {
	name: 'UnderwriterList',
	components: { MBtn },
	route: {
		path: '/SSAM/underwriters',
		meta: { requiresAuth: true, title: 'Underwriter Management' },
	},
	setup() {
		const api = useUnderwriterApi();
		const route = useRoute();
		const tell = inject('tell') as Tell;

		const loading = ref(false);
		const underwriters = ref<UnderwriterViewModel[]>([]);
		const totalCount = ref(0);
		const page = ref(1);
		const pageSize = ref(25);
		const includeExpired = ref(false);
		const lastNameSearch = ref(typeof route.query.lastName === 'string' ? route.query.lastName : '');

		let searchDebounce: ReturnType<typeof setTimeout> | null = null;

		const showExpireDialog = ref(false);
		const expiring = ref(false);
		const pendingExpire = ref<UnderwriterViewModel | null>(null);

		const headers = [
			{ title: 'Profile', key: 'profile', sortable: false, align: 'start' as const },
			{ title: 'Last Name', key: 'lastName', sortable: false, align: 'start' as const },
			{ title: 'First Name', key: 'firstName', sortable: false, align: 'start' as const },
			{ title: 'Middle Name', key: 'middleName', sortable: false, align: 'start' as const },
			{ title: 'User Group Profile', key: 'userGroupProfile', sortable: false, align: 'start' as const },
			{ title: 'Status', key: 'status', sortable: false, align: 'start' as const },
			{ title: '', key: 'actions', sortable: false, align: 'end' as const, width: 120 },
		];

		async function loadUnderwriters(p: number, ps: number) {
			loading.value = true;
			try {
				const result = await api.getUnderwriters(p, ps, includeExpired.value, lastNameSearch.value || undefined);
				underwriters.value = result.items;
				totalCount.value = result.totalCount;
			} catch (e: unknown) {
				tell.error(`Error loading underwriters: ${(e as Error).message}`);
			} finally {
				loading.value = false;
			}
		}

		function onTableOptions(options: { page: number; itemsPerPage: number }) {
			page.value = options.page;
			pageSize.value = options.itemsPerPage;
			loadUnderwriters(options.page, options.itemsPerPage);
		}

		function onFilterChange() {
			page.value = 1;
			loadUnderwriters(1, pageSize.value);
		}

		function searchNow() {
			if (searchDebounce) clearTimeout(searchDebounce);
			page.value = 1;
			loadUnderwriters(1, pageSize.value);
		}

		function searchDebounced() {
			if (searchDebounce) clearTimeout(searchDebounce);
			searchDebounce = setTimeout(searchNow, 350);
		}

		function confirmExpire(item: UnderwriterViewModel) {
			pendingExpire.value = item;
			showExpireDialog.value = true;
		}

		async function executeExpire() {
			if (!pendingExpire.value) return;
			expiring.value = true;
			try {
				await api.expireUnderwriter(pendingExpire.value.profile);
				tell.success(`Expired ${pendingExpire.value.profile.toUpperCase()}.`);
				showExpireDialog.value = false;
				pendingExpire.value = null;
				await loadUnderwriters(page.value, pageSize.value);
			} catch (e: unknown) {
				tell.error(`Error expiring underwriter: ${(e as Error).message}`);
			} finally {
				expiring.value = false;
			}
		}

		return {
			loading,
			underwriters,
			totalCount,
			page,
			pageSize,
			headers,
			includeExpired,
			lastNameSearch,
			onTableOptions,
			onFilterChange,
			searchNow,
			searchDebounced,
			showExpireDialog,
			expiring,
			pendingExpire,
			confirmExpire,
			executeExpire,
		};
	},
};
</script>

<style scoped>
:deep(.v-data-table__td) {
	text-align: start !important;
}

.search-field {
	max-width: var(--app-size-drawer);
}
</style>
